import { useLayoutEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@bw/shared/hooks";
import type { FinanceApi } from "@bw/shared/api";
import type { FinanceAccountRefreshResponse } from "@bw/shared/types/generated";
import type { FinanceConnectTrustedActorScope } from "@/components/finance-connect/FinanceConnectEmbed";
import { reconcileFinanceConnectContext, retireFinanceConnectScope } from "@/components/finance-connect/lifecycle";

export function useFinanceContext(
  orgId: string | null | undefined,
  api: FinanceApi,
  discoveryEnabled = true,
) {
  const { user, loading } = useAuth();
  const cache = useQueryClient();
  const revision = null;
  const admitted = discoveryEnabled && !loading && Boolean(user && orgId);
  const identity = [user?.uid ?? null, revision, orgId ?? null] as const;
  const liveIdentity = useRef(JSON.stringify(identity)); liveIdentity.current = JSON.stringify(identity);
  const active = useRef(true);
  useLayoutEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const query = useQuery({ queryKey: ["org-finance", ...identity, "context"],
    queryFn: ({ signal }) => api.getContext(orgId as string, signal), enabled: admitted,
    staleTime: 0, retry: false, refetchOnMount: "always", refetchOnWindowFocus: true });
  const context = admitted && query.isSuccess && query.isFetchedAfterMount &&
    query.data.org_id === orgId && query.data.work_session_revision === revision ? query.data : null;
  const actorScope: FinanceConnectTrustedActorScope | null = context && user ? {
    actorUserId: user.uid, orgId: context.org_id, workSessionRevision: revision,
    bindingGeneration: context.binding_generation,
  } : null;
  const previous = useRef<{ scope: FinanceConnectTrustedActorScope; generation: string; configuration: string } | null>(null);
  useLayoutEffect(() => {
    if (user && orgId) reconcileFinanceConnectContext(user.uid, orgId, context);
    const before = previous.current;
    const changed = before && (!actorScope || JSON.stringify(before.scope) !== JSON.stringify(actorScope) ||
      before.generation !== context?.authority_generation || before.configuration !== JSON.stringify(context?.stripe_configuration));
    if (before) for (const purpose of ["onboarding", "account_settings", "disputes"] as const) {
      if (changed || !context?.allowed_session_purposes.includes(purpose)) retireFinanceConnectScope(before.scope, purpose);
    }
    if (changed && before) cache.removeQueries({ queryKey: ["org-finance", before.scope.actorUserId,
      before.scope.workSessionRevision, before.scope.orgId, "data", before.scope.bindingGeneration, before.generation] });
    previous.current = actorScope && context ? { scope: actorScope, generation: context.authority_generation,
      configuration: JSON.stringify(context.stripe_configuration) } : null;
  }, [actorScope?.actorUserId, actorScope?.orgId, actorScope?.bindingGeneration, actorScope?.workSessionRevision,
    context?.authority_generation, JSON.stringify(context?.allowed_session_purposes), JSON.stringify(context?.stripe_configuration), cache]);
  const dataKey = ["org-finance", ...identity, "data", context?.binding_generation,
    context?.authority_generation] as const;
  const observed = useQuery<FinanceAccountRefreshResponse>({ queryKey: [...dataKey, "readiness"],
    queryFn: () => api.refreshAccount(orgId as string), enabled: false });
  const refreshAccount = async () => {
    const captured = JSON.stringify(identity);
    if (!context?.account_bound || !context.allowed_actions.includes("org:finance_read") ||
        context.stripe_configuration.state !== "configured") throw new Error("Payment account status is unavailable.");
    const readiness = await api.refreshAccount(orgId as string);
    if (!active.current || liveIdentity.current !== captured || readiness.org_id !== orgId ||
        readiness.work_session_revision !== revision) throw new Error("Payment account status changed. Refresh and try again.");
    const refreshed = await query.refetch();
    const current = refreshed.isSuccess ? refreshed.data : null;
    if (!active.current || liveIdentity.current !== captured || !current?.account_bound ||
        current.stripe_configuration.state !== "configured" || !current.allowed_actions.includes("org:finance_read") ||
        current.org_id !== readiness.org_id || current.work_session_revision !== readiness.work_session_revision ||
        current.binding_generation !== readiness.binding_generation || current.authority_generation !== readiness.authority_generation)
      throw new Error("Payment account status changed. Refresh and try again.");
    cache.setQueryData(["org-finance", ...identity, "data", readiness.binding_generation,
      readiness.authority_generation, "readiness"], readiness);
    return readiness;
  };
  const accepted = observed.data && context && observed.data.org_id === context.org_id &&
    observed.data.work_session_revision === context.work_session_revision &&
    observed.data.binding_generation === context.binding_generation &&
    observed.data.authority_generation === context.authority_generation ? observed.data : null;
  return { context, actorScope, query, dataKey, refreshAccount,
    readiness: accepted ?? context?.provider_readiness ?? null, pending: admitted && !context && !query.isError };
}
